/**
 * Retrieval Page Component
 * 
 * This component displays the retrieval requests management interface
 * with filtering, search, and retrieval management capabilities.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { useTranslations } from '../context/TranslationsContext';
import { retrievalAPI } from '../api';
import RetrievalTable from '../components/retrieval/RetrievalTable';
import RetrievalDetailsModal from '../components/retrieval/RetrievalDetailsModal';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';

const RetrievalPage = () => {
  const { state, actions } = useApp();
  const { translations } = useTranslations();
  const t = translations[state.currentLanguage];

  const [retrievalRequests, setRetrievalRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    limit: 10,
    skip: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchRetrievalRequests = async () => {
      if (!state.token) return;

      actions.setLoading(true);
      try {
        const data = await retrievalAPI.getRetrievalRequests(state.token, filters.limit, filters.skip);
        setRetrievalRequests(data.requests || []);
      } catch (error) {
        console.error('Error fetching retrieval requests:', error);
      } finally {
        actions.setLoading(false);
      }
    };

    fetchRetrievalRequests();
  }, [state.token, filters]);

  // Filter requests based on search term
  const filteredRequests = useMemo(() => {
    if (!searchTerm) return retrievalRequests;
    
    return retrievalRequests.filter(request => 
      request.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.customer_phone?.includes(searchTerm) ||
      request.order_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [retrievalRequests, searchTerm]);

  // Paginate filtered requests
  const paginatedRequests = useMemo(() => {
    const startIndex = (currentPage - 1) * filters.limit;
    const endIndex = startIndex + filters.limit;
    return filteredRequests.slice(startIndex, endIndex);
  }, [filteredRequests, currentPage, filters.limit]);

  const totalPages = Math.ceil(filteredRequests.length / filters.limit);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  };

  const handleSearchChange = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleApproveRetrieval = async (orderId, adminNotes = '') => {
    try {
      await retrievalAPI.approveRetrieval(state.token, orderId, adminNotes);
      
      // Refresh requests
      const data = await retrievalAPI.getRetrievalRequests(state.token, filters.limit, filters.skip);
      setRetrievalRequests(data.requests || []);
      alert('تم اعتماد طلب الاسترجاع بنجاح');
    } catch (error) {
      console.error('Error approving retrieval:', error);
      alert('خطأ في اعتماد طلب الاسترجاع');
    }
  };

  const handleStartDelivery = async (orderId, adminNotes = '') => {
    try {
      await retrievalAPI.startDelivery(state.token, orderId, adminNotes);
      
      // Refresh requests
      const data = await retrievalAPI.getRetrievalRequests(state.token, filters.limit, filters.skip);
      setRetrievalRequests(data.requests || []);
      alert('تم بدء عملية التوصيل بنجاح');
    } catch (error) {
      console.error('Error starting delivery:', error);
      alert('خطأ في بدء عملية التوصيل');
    }
  };

  const handleCompleteRetrieval = async (orderId, adminNotes = '') => {
    try {
      await retrievalAPI.completeRetrieval(state.token, orderId, adminNotes);
      
      // Refresh requests
      const data = await retrievalAPI.getRetrievalRequests(state.token, filters.limit, filters.skip);
      setRetrievalRequests(data.requests || []);
      alert('تم إكمال عملية الاسترجاع بنجاح');
    } catch (error) {
      console.error('Error completing retrieval:', error);
      alert('خطأ في إكمال عملية الاسترجاع');
    }
  };

  if (state.loading) {
    return <div className="loading">جاري التحميل...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h2 className="section-title">{t.retrieval}</h2>
        <div className="page-actions flex gap-4">
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            placeholder="البحث في طلبات الاسترجاع..."
            className="w-64"
          />
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange({ status: e.target.value })}
            className="form-input"
          >
            <option value="">جميع الحالات</option>
            <option value="retrieval_requested">{t.retrieval_requested}</option>
            <option value="retrieval_approved">{t.retrieval_approved}</option>
            <option value="retrieval_delivering">{t.retrieval_delivering}</option>
            <option value="retrieval_completed">{t.retrieval_completed}</option>
          </select>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <RetrievalTable
          requests={paginatedRequests}
          onViewDetails={(request) => setSelectedRequest(request)}
          onApprove={handleApproveRetrieval}
          onStartDelivery={handleStartDelivery}
          onComplete={handleCompleteRetrieval}
        />
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalItems={filteredRequests.length}
          itemsPerPage={filters.limit}
        />
      </div>

      {selectedRequest && (
        <RetrievalDetailsModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
        />
      )}
    </div>
  );
};

export default RetrievalPage;
